import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import authService from '../../services/authService';

const ReactivateAccount = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const hasAttempted = useRef(false); // Prevent double execution in React Strict Mode

  useEffect(() => {
    const reactivateAccount = async () => {
      // Prevent multiple executions
      if (hasAttempted.current) {
        return;
      }
      hasAttempted.current = true;

      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid reactivation link. Please request a new one.');
        return;
      }

      try {
        await authService.reactivateWithToken(token);
        setStatus('success');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      } catch (error) {
        setStatus('error');
        setErrorMessage(error.message || 'Failed to reactivate account. The link may have expired.');
      }
    };

    reactivateAccount();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          {/* Verifying State */}
          {status === 'verifying' && (
            <>
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-white">Reactivating Account</h1>
                <p className="text-white/80 mt-2">Please wait while we verify your request...</p>
              </div>
              <div className="px-6 py-8">
                <div className="flex items-center justify-center">
                  <div className="animate-pulse flex space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full animation-delay-200"></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full animation-delay-400"></div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-white">Account Reactivated!</h1>
                <p className="text-white/80 mt-2">Welcome back to Tasklina</p>
              </div>
              <div className="px-6 py-8 text-center">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-6">
                  <p className="text-green-800 dark:text-green-300">
                    Your account has been successfully reactivated. You can now log in and access all your data.
                  </p>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Redirecting to login in 3 seconds...
                </p>
                <Link
                  to="/auth/login"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Go to Login Now
                </Link>
              </div>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-white">Reactivation Failed</h1>
                <p className="text-white/80 mt-2">Unable to reactivate your account</p>
              </div>
              <div className="px-6 py-8 text-center">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
                  <p className="text-red-800 dark:text-red-300">
                    {errorMessage}
                  </p>
                </div>
                <div className="space-y-3">
                  <Link
                    to="/auth/login"
                    className="block w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all text-center"
                  >
                    Go to Login
                  </Link>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Need help? <a href="mailto:support@Tasklina.com" className="text-blue-600 dark:text-blue-400 hover:underline">Contact Support</a>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactivateAccount;
